import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";

export default function CompleteTheLook({ productId, color }) {
  const { addItem } = useCart();
  const [items, setItems] = useState([]);
  const [picking, setPicking] = useState(null);
  const [pct, setPct] = useState(null);

  useEffect(() => {
    api.get("/site/status").then((r) => setPct(r.data.look_discount_percent ?? 10)).catch(() => setPct(10));
  }, []);

  useEffect(() => {
    if (!productId) return;
    let live = true;
    api.get(`/products/${productId}/complete-look`, { params: color ? { color } : {} })
      .then((r) => { if (live) { setItems(r.data.items || []); setPicking(null); } })
      .catch(() => { if (live) setItems([]); });
    return () => { live = false; };
  }, [productId, color]);

  if (!items.length) return null;

  const qtyLeft = (p, s) => {
    const v = p.stock?.[p.matched_color]?.[s];
    return v === undefined || v === null ? Infinity : v;
  };
  const isOut = (p, s) => (p.sold_out_sizes || []).includes(s) || qtyLeft(p, s) === 0;

  const quickAdd = (p, size) => {
    addItem(p, size, p.matched_color);
    toast.success(`${p.name} added to bag${size ? ` — ${p.matched_color} / ${size}` : ""}.`);
    setPicking(null);
  };

  const onQuickAdd = (p) => {
    const sizes = p.sizes || [];
    if (sizes.length <= 1) { quickAdd(p, sizes[0] || null); return; }
    setPicking(picking === p.id ? null : p.id);
  };

  return (
    <div className="mt-28" data-testid="complete-the-look">
      <p className="eyebrow mb-3">Complete the Look</p>
      <p className="text-graphite text-sm max-w-md" data-testid="ctl-subtitle">
        Pieces matched to pair with this one{color ? ` — built around ${color}` : ""}.
      </p>
      {pct > 0 ? (
        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-signal mt-2 mb-10" data-testid="ctl-discount-note">
          Pair a top with a bottom — {pct}% off applied at checkout
        </p>
      ) : <div className="mb-10" />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
        {items.map((p, i) => (
          <div key={p.id} className="relative">
            <ProductCard product={p} index={i} onQuickAdd={() => onQuickAdd(p)} />
            {picking === p.id && (
              <div className="absolute inset-x-0 top-0 aspect-square z-20 bg-ink/90 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-4"
                data-testid={`ctl-size-picker-${p.id}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-chalk/80 text-center">
                  Select size — {p.matched_color}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {p.sizes.map((s) => (
                    <button key={s} disabled={isOut(p, s)} onClick={() => quickAdd(p, s)}
                      data-testid={`ctl-size-${p.id}-${s}`}
                      className={`font-mono text-xs min-w-[44px] h-10 border transition-colors ${
                        isOut(p, s)
                          ? "border-border text-chalk/30 line-through decoration-pulse"
                          : "border-chalk/60 text-chalk hover:bg-chalk hover:text-ink"
                      }`}>{s}</button>
                  ))}
                </div>
                <button onClick={() => setPicking(null)} data-testid={`ctl-size-cancel-${p.id}`}
                  className="font-mono text-[10px] uppercase tracking-wider text-graphite hover:text-pulse mt-1">
                  Cancel
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
